<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { get } from 'svelte/store'

    import type { GenOrder, IOrder } from '../order'
    import { cloneDeep, isEqual } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'

    import type { IAddress, IAddressGroup } from 'wdom/services/contacts'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import ContactEditor from 'wdom/components/contacts/ContactEditor.svelte'
    import OrderAddressDisplay from '../OrderAddressDisplay.svelte'
    import OrderAddressSelection from '../OrderAddressSelection.svelte'

    const BASE_FIELDS = ['name', 'org', 'city', 'sp', 'postal_code', 'country', 'voice', 'fax', 'mobile']
    const BASE_REQ_FIELDS = ['name', 'city', 'country', 'street', 'country', 'postal_code', 'sp', 'any-phone']

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const Order = getContext('Order') as GenOrder

    let status: 'not-found' | 'ready' | 'loading' | 'error' = $state('loading')
    unsubs.push(
        Order.status.subscribe((v) => {
            status = v
        }),
    )

    let aStatus: 'ready' | 'saving' | 'loading' | 'copying' | undefined = $state()

    let order: IOrder = $state(get(Order.orderStore))

    // @ts-expect-error
    const address: IAddress | undefined | null = $derived(status == 'ready' ? order.billing_address : null)

    // ############################################################################  Address Book

    let addresses: IAddressGroup[] = $state([])
    let loadedAddressesUserId: string
    // let mainAddressIsValid = null
    async function loadAddresses() {
        if (order.owner == loadedAddressesUserId) {
            return
        }
        aStatus = 'loading'

        try {
            addresses = await Order.loadAddresses()
        } catch {
            // Just be silent for now
            addresses = []
        }
        loadedAddressesUserId = order.owner
        aStatus = 'ready'
    }

    unsubs.push(
        Order.orderStore.subscribe((v) => {
            order = v
            if (aStatus == null) loadAddresses()
        }),
    )

    // The matching address that was used on the cart, if any
    let addressBookAddressId: string | undefined = $state()
    // If true, the matching address matches in terms of data
    let addressMatchesBookSource: boolean = $state(false)

    // Find the original matching address in the book, and checks if it changed
    function matchCartAddress() {
        addressBookAddressId = undefined
        addressMatchesBookSource = true

        if (address == null || address._source == null) {
            return
        }

        let source = addresses.filter((ladd) => ladd.id == address._source)[0]
        if (!source) {
            return
        }

        addressBookAddressId = source.id

        let changedFields: any[] = []
        for (const field of BASE_FIELDS) {
            let svalue = source.source[field] ? source.source[field] : null
            let avalue = address[field] ? address[field] : null
            if (avalue !== svalue) changedFields.push(field)
        }

        if (!isEqual(source.source.street, address.street)) changedFields.push('street')

        if (changedFields.length) addressMatchesBookSource = false
    }

    $effect(() => {
        if (status == 'ready' && address && addresses != null) matchCartAddress()
    })

    // ################################################################################# Address Edition

    let contactGroupEdit: any = $state(null)
    function editAddress() {
        let cgroup = {
            source: cloneDeep(address),
            // fieldStatus: {},
            // hiddenFields: [],
            fields: new Set([...BASE_FIELDS, 'street']),
            requiredFields: new Set([...BASE_REQ_FIELDS, 'street']),
            edit: cloneDeep(address),
            invalidFields: {
                contextual: new Set(),
            },
        }
        contactGroupEdit = cgroup
    }

    async function importEdition(cg: any) {
        if (!contactGroupEdit) return
        const data = cg.edit
        contactGroupEdit = null
        aStatus = 'saving'

        await Order.setBillingAddress(data)

        aStatus = 'ready'
    }

    let showAddressBook = $state(false)
    $effect(() => {
        if (status != 'ready' && showAddressBook) showAddressBook = false
    })

    async function loadFromAddressBook(addressId: string) {
        showAddressBook = false

        aStatus = 'copying'
        await Order.setBillingAddressFromBook(addressId)
        aStatus = 'ready'
    }
</script>

{#if contactGroupEdit != null}
    <ContactEditor
        context="cart"
        contactGroup={contactGroupEdit}
        close={() => {
            contactGroupEdit = null
        }}
        save={(cg) => importEdition(cg)}
    />
{/if}

{#if showAddressBook}
    <OrderAddressSelection {addresses} selectedId={addressBookAddressId ? addressBookAddressId : null} modified={!!(addressBookAddressId && !addressMatchesBookSource)} close={() => (showAddressBook = false)} apply={(e) => loadFromAddressBook(e.id)} />
{/if}

<div class="card">
    <div class="card-title text-primary">{$_('Billing address')}</div>
    {#if status != 'ready'}
        <div class="card-body">
            <LoadingPattern pattern={['x', 'x', 'x', 'x']} />
        </div>
    {:else}
        <div class="card-body">
            <OrderAddressDisplay address={status == 'ready' ? address : null} />
        </div>
        {#if ['draft', 'ready', 'unpaid'].includes(order.status)}
            <div class="card-footer d-grid">
                <button type="button" class="btn btn-outline-secondary btn-block" style="white-space: normal" onclick={() => (showAddressBook = true)} disabled={status != 'ready' || aStatus != 'ready'}>
                    {#if aStatus == 'loading'}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Loading…')}
                    {:else if aStatus == 'copying'}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Saving…')}
                    {:else}
                        {$_('Copy from user’s address book')}
                    {/if}
                </button>
                <button type="button" class="btn btn-outline-secondary btn-block mt-3" onclick={() => editAddress()} disabled={status != 'ready' || aStatus != 'ready'}>
                    {#if aStatus == 'saving'}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Saving…')}
                    {:else}
                        {$_('Edit')}
                    {/if}
                </button>
            </div>
        {/if}
    {/if}
</div>
